import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CodeIcon from "@mui/icons-material/Code";
import HttpIcon from "@mui/icons-material/Http";
import DescriptionIcon from "@mui/icons-material/Description";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ApiIcon from "@mui/icons-material/Api";
import FolderIcon from "@mui/icons-material/Folder";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TerminalIcon from "@mui/icons-material/Terminal";
import InfoIcon from "@mui/icons-material/Info";
import ErrorIcon from "@mui/icons-material/Error";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MarkdownPreview from "@uiw/react-markdown-preview";

const JsonViewer = ({ data }) => {
  const [copied, setCopied] = useState(false);

  const getJsonString = () => {
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return data;
      }
    }
    return JSON.stringify(data, null, 2);
  };

  const jsonString = getJsonString();

  const formatJson = (str) =>
    str
      ?.replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
        (match) => {
          let cls = "json-number";
          if (/^"/.test(match)) {
            cls = /:$/.test(match) ? "json-key" : "json-string";
          } else if (/true|false/.test(match)) {
            cls = "json-boolean";
          } else if (/null/.test(match)) {
            cls = "json-null";
          }
          return `<span class="${cls}">${match}</span>`;
        },
      );

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <Box className="json-container">
      <Box className="copy-button">
        <Tooltip title={copied ? "Copiato!" : "Copia"}>
          <IconButton
            size="small"
            onClick={handleCopy}
            className={`copy-button-inner ${copied ? 'copy-success' : ''}`}
          >
            {copied ? (
              <CheckCircleOutlineIcon fontSize="small" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        component="pre"
        className="json-content"
        dangerouslySetInnerHTML={{ __html: formatJson(jsonString) }}
      />
    </Box>
  );
};

const MethodBadge = ({ method }) => {
  const up = method?.toUpperCase() || "UNKNOWN";
  const methodClass = `method-${up.toLowerCase()}` || "method-unknown";

  return (
    <Chip
      label={up}
      size="small"
      className={`method-badge ${methodClass}`}
    />
  );
};

const ParametersTable = ({ parameters, title }) => {
  if (!parameters || parameters.length === 0) {
    return (
      <Box className="params-container">
        <Typography variant="subtitle2" className="params-title">
          {title}
        </Typography>
        <Typography variant="body2" className="params-empty">
          Nessun parametro definito
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="params-container">
      <Typography variant="subtitle2" className="params-title">
        {title}
      </Typography>
      <TableContainer className="params-table-container">
        <Table size="small" aria-label={`Tabella ${title}`}>
          <TableHead>
            <TableRow className="params-table-head">
              <TableCell width="25%" className="params-table-header">Nome</TableCell>
              <TableCell width="35%" className="params-table-header">Valore</TableCell>
              <TableCell width="40%" className="params-table-header">Descrizione</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parameters.map((param, index) => (
              <TableRow
                key={index}
                hover
                className={index % 2 !== 0 ? 'params-table-row-odd' : ''}
              >
                <TableCell component="th" scope="row" className="params-table-param-name">
                  {param.key || "-"}
                </TableCell>
                <TableCell className="params-table-param-value">
                  <Box component="code" className="params-table-code">
                    {param.value || "-"}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" className="params-table-description">
                    {param.description || "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const UrlDisplay = ({ url }) => {
  if (!url) return <Typography variant="body2">URL non specificato</Typography>;

  if (typeof url === "string") {
    try {
      const parsedUrl = new URL(url);
      return (
        <Box className="url-display">
          <Typography component="span" className="url-protocol">
            {parsedUrl.protocol}//
          </Typography>
          <Typography component="span" className="url-host">
            {parsedUrl.host}
          </Typography>
          <Typography component="span" className="url-path">
            {parsedUrl.pathname}
          </Typography>
          {parsedUrl.search && (
            <Typography component="span" className="url-query">
              {parsedUrl.search}
            </Typography>
          )}
          {parsedUrl.hash && (
            <Typography component="span" className="url-hash">
              {parsedUrl.hash}
            </Typography>
          )}
        </Box>
      );
    } catch {
      return (
        <Typography variant="body2" className="url-display">
          {url}
        </Typography>
      );
    }
  }

  const host = Array.isArray(url.host) ? url.host.join(".") : url.host || "";
  const path = Array.isArray(url.path)
    ? "/" + url.path.join("/")
    : url.path || "";

  return (
    <Box className="url-display">
      {url.protocol && (
        <Typography component="span" className="url-protocol">
          {url.protocol}://
        </Typography>
      )}
      <Typography component="span" className="url-host">
        {host}
      </Typography>
      <Typography component="span" className="url-path">
        {path}
      </Typography>
      {url.query && url.query.length > 0 && (
        <Typography component="span" className="url-query">
          ?{url.query.map((q) => `${q.key}=${q.value}`).join("&")}
        </Typography>
      )}
    </Box>
  );
};

const RequestViewer = ({ request }) => {
  const [tabValue, setTabValue] = useState(0);
  const [codeLanguage, setCodeLanguage] = useState("curl");

  if (!request) return null;

  const url = request.url || {};
  const urlRaw = typeof url === "string" ? url : url.raw || "";

  const queryParams = request.url?.query || [];
  const pathParams = request.url?.variable || [];
  const headerParams = request.header || [];
  const bodyParams = request.body;

  const renderBodyContent = () => {
    if (!bodyParams) {
      return (
        <Alert severity="info" variant="outlined" sx={{ backgroundColor: "transparent" }}>
          <Typography variant="body2">
            Nessun body definito per questa richiesta
          </Typography>
        </Alert>
      );
    }

    const mode = bodyParams.mode || "";

    if (mode === "raw") {
      const lang = bodyParams.options?.raw?.language || "text";
      const rawContent = bodyParams.raw || "";

      try {
        if (lang === "json") {
          const jsonData =
            typeof rawContent === "string"
              ? JSON.parse(rawContent)
              : rawContent;
          return (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Chip
                  label="application/json"
                  size="small"
                  className="content-type-chip"
                />
              </Box>
              <JsonViewer data={jsonData} />
            </Box>
          );
        }
      } catch {
        /* ignore */
      }

      return (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Chip
              label={`text/${lang}`}
              size="small"
              className="content-type-chip"
            />
          </Box>
          <Box className="json-container">
            <Box className="copy-button">
              <Tooltip title="Copia">
                <IconButton
                  size="small"
                  onClick={() => navigator.clipboard.writeText(rawContent)}
                  className="copy-button-inner"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box component="pre" className="code-content">
              <code>{rawContent}</code>
            </Box>
          </Box>
        </Box>
      );
    }

    if (mode === "formdata" && bodyParams.formdata) {
      return (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Chip
              label="multipart/form-data"
              size="small"
              className="content-type-chip form-data"
            />
          </Box>
          <ParametersTable
            parameters={bodyParams.formdata}
            title="Form Data Parameters"
          />
        </Box>
      );
    }

    if (mode === "urlencoded" && bodyParams.urlencoded) {
      return (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Chip
              label="application/x-www-form-urlencoded"
              size="small"
              className="content-type-chip url-encoded"
            />
          </Box>
          <ParametersTable
            parameters={bodyParams.urlencoded}
            title="URL Encoded Parameters"
          />
        </Box>
      );
    }

    return <JsonViewer data={bodyParams} />;
  };

  const generateCode = (language) => {
    const method = request.method || "GET";
    const headers = headerParams.reduce((acc, h) => {
      if (h?.key) acc[h.key] = h.value ?? "";
      return acc;
    }, {});
    const body = bodyParams?.raw || "";

    switch (language) {
      case "curl": {
        let curl = `curl -X ${method} "${urlRaw}"`;
        Object.entries(headers).forEach(([key, value]) => {
          curl += ` \\\n  -H "${key}: ${value}"`;
        });
        if (body) curl += ` \\\n  -d '${body.replace(/'/g, "'\\''")}'`;
        return curl;
      }
      case "javascript":
        return `fetch("${urlRaw}", {
  method: "${method}",
  headers: {
${Object.entries(headers)
  .map(([k, v]) => `    "${k}": "${v}"`)
  .join(",\n")}
  }${body ? `,\n  body: '${body.replace(/'/g, "'\\''")}'` : ""}
})
  .then(r => r.json())
  .then(console.log)
  .catch(console.error);`;
      case "python":
        return `import requests

response = requests.${method.toLowerCase()}(
    "${urlRaw}",
    headers={
${Object.entries(headers)
  .map(([k, v]) => `        "${k}": "${v}"`)
  .join(",\n")}
    }${body ? `,\n    data='''${body.replace(/'/g, "''")}'''` : ""}
)

print(response.text)`;
      case "php":
        return `<?php
$ch = curl_init();
$headers = [
${Object.entries(headers)
  .map(([k, v]) => `  "${k}: ${v}"`)
  .join(",\n")}
];

curl_setopt_array($ch, [
  CURLOPT_URL => "${urlRaw}",
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_CUSTOMREQUEST => "${method}",
  CURLOPT_HTTPHEADER => $headers,${body ? `\n  CURLOPT_POSTFIELDS => '${body.replace(/'/g, "\\'")}',` : ""}
]);

$response = curl_exec($ch);
if ($response === false) {
  echo "cURL Error: " . curl_error($ch);
} else {
  echo $response;
}
curl_close($ch);`;
      default:
        return "";
    }
  };

  return (
    <Box className="request-container">
      <Box className="request-header">
        <MethodBadge method={request.method} />
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <UrlDisplay url={url} />
        </Box>
      </Box>

      {request.description && (
        <Box className="request-description">
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
            <InfoIcon className="request-description-icon" />
            <Box sx={{ width: "100%" }}>
              <MarkdownPreview
                source={request.description}
                style={{
                  backgroundColor: "transparent",
                  fontSize: "0.92rem",
                  color: "transparent"
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Box className="request-tabs">
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          textColor="primary"
          indicatorColor="primary"
          aria-label="Tabs richiesta API"
        >
          <Tab
            icon={<DescriptionIcon fontSize="small" />}
            iconPosition="start"
            label="Parametri"
            className="request-tab"
          />
          <Tab
            icon={<HttpIcon fontSize="small" />}
            iconPosition="start"
            label={`Headers (${headerParams?.length || 0})`}
            className="request-tab"
          />
          <Tab
            icon={<CodeIcon fontSize="small" />}
            iconPosition="start"
            label="Body"
            className="request-tab"
          />
          <Tab
            icon={<LinkIcon fontSize="small" />}
            iconPosition="start"
            label="URL"
            className="request-tab"
          />
          <Tab
            icon={<TerminalIcon fontSize="small" />}
            iconPosition="start"
            label="Codice"
            className="request-tab"
          />
        </Tabs>
      </Box>

      <Box className="request-content">
        {tabValue === 0 && (
          <Box>
            {queryParams.length > 0 && (
              <ParametersTable
                parameters={queryParams}
                title="Query Parameters"
              />
            )}
            {pathParams.length > 0 && (
              <ParametersTable parameters={pathParams} title="Path Variables" />
            )}
            {queryParams.length === 0 && pathParams.length === 0 && (
              <Alert
                severity="info"
                variant="outlined"
                sx={{ backgroundColor: "transparent" }}
              >
                <Typography variant="body2">
                  Questa richiesta non ha parametri di query o path definiti
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            {headerParams && headerParams.length > 0 ? (
              <TableContainer className="params-table-container">
                <Table size="small" aria-label="HTTP Headers">
                  <TableHead>
                    <TableRow className="params-table-head">
                      <TableCell width="30%" className="params-table-header">Nome</TableCell>
                      <TableCell width="60%" className="params-table-header">Valore</TableCell>
                      <TableCell width="10%" className="params-table-header">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {headerParams.map((header, index) => (
                      <TableRow
                        key={index}
                        hover
                        className={index % 2 !== 0 ? 'params-table-row-odd' : ''}
                      >
                        <TableCell component="th" scope="row" className="params-table-param-name">
                          {header.key || "-"}
                        </TableCell>
                        <TableCell className="params-table-param-value">
                          <Box component="code" className="params-table-code">
                            {header.value || "-"}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Copia valore">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${header.key}: ${header.value}`,
                                );
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert
                severity="info"
                variant="outlined"
                sx={{ backgroundColor: "transparent" }}
              >
                <Typography variant="body2">
                  Questa richiesta non ha header HTTP definiti
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {tabValue === 2 && <Box>{renderBodyContent()}</Box>}

        {tabValue === 3 && (
          <Box sx={{ mt: 1 }}>
            <Box className="url-paper">
              <Typography variant="subtitle2" className="url-section-title">
                URL completo
              </Typography>

              <Box className="url-display-container">
                <Box className="copy-button">
                  <Tooltip title="Copia URL">
                    <IconButton
                      size="small"
                      onClick={() => navigator.clipboard.writeText(urlRaw)}
                      className="copy-button-inner"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Typography variant="body2" className="url-full-text">
                  {urlRaw || "N/D"}
                </Typography>
              </Box>

              {url.host && (
                <Box className="url-part-container">
                  <Typography variant="subtitle2" className="url-part-title">
                    Host
                  </Typography>
                  <Typography variant="body2" className="url-part-value">
                    {Array.isArray(url.host) ? url.host.join(".") : url.host}
                  </Typography>
                </Box>
              )}

              {url.path && (
                <Box className="url-part-container">
                  <Typography variant="subtitle2" className="url-part-title">
                    Path
                  </Typography>
                  <Typography variant="body2" className="url-part-value">
                    /{Array.isArray(url.path) ? url.path.join("/") : url.path}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {tabValue === 4 && (
          <Box>
            <Box className="code-controls">
              <FormControl size="small" className="code-language-select">
                <Select
                  value={codeLanguage}
                  onChange={(e) => setCodeLanguage(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="curl">cURL</MenuItem>
                  <MenuItem value="javascript">JavaScript</MenuItem>
                  <MenuItem value="python">Python</MenuItem>
                  <MenuItem value="php">PHP</MenuItem>
                </Select>
              </FormControl>

              <Tooltip title="Copia codice">
                <IconButton
                  onClick={() =>
                    navigator.clipboard.writeText(generateCode(codeLanguage))
                  }
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Box className="code-container">
              <Box component="pre" className="code-content">
                <code>{generateCode(codeLanguage)}</code>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ItemViewer = ({ item, depth = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!item) return null;

  if (item.request) {
    return (
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((v) => !v)}
        className="item-accordion"
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={`item-summary ${expanded ? 'expanded' : ''}`}
        >
          <Box className="item-summary-content">
            <KeyboardArrowRightIcon className="item-arrow-icon" />
            <Typography className="item-title">
              {item.name}
            </Typography>
            <MethodBadge method={item.request.method} />
          </Box>
        </AccordionSummary>
        <AccordionDetails className="item-details">
          <RequestViewer request={item.request} />
        </AccordionDetails>
      </Accordion>
    );
  }
  return <FolderViewer folder={item} depth={depth} />;
};

const FolderViewer = ({ folder, depth = 0 }) => {
  const [expanded, setExpanded] = useState(depth === 0);
  if (!folder) return null;
  const isRoot = depth === 0;
  const folderClass = isRoot ? 'folder-accordion-root' : 'folder-accordion';
  const summaryClass = isRoot ? 'folder-summary-root' : 'folder-summary';
  const iconClass = isRoot ? 'folder-icon-root' : 'folder-icon';
  const titleClass = isRoot ? 'folder-title-root' : 'folder-title';

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((v) => !v)}
      disableGutters
      className={folderClass}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={`${summaryClass} ${expanded ? 'expanded' : ''}`}
        style={{ paddingLeft: Math.min(depth * 1.25, 4) * 8 + 'px' }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FolderIcon className={iconClass} />
          <Typography className={titleClass}>
            {folder.name}
          </Typography>
          {folder.item && folder.item.length > 0 && (
            <Chip
              label={folder.item.length}
              size="small"
              className="folder-chip"
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails className="folder-details">
        {folder.description && (
          <Box className="folder-description">
            <MarkdownPreview
              source={folder.description}
              style={{
                backgroundColor: "transparent",
                fontSize: "0.9rem",
              }}
            />
          </Box>
        )}

        {folder.item?.map((item, index) => (
          <Box key={index} sx={{ mb: 1.5 }}>
            {item.request ? (
              <ItemViewer item={item} depth={depth + 1} />
            ) : (
              <FolderViewer folder={item} depth={depth + 1} />
            )}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const GridView = ({ items }) => {
  const flattenItems = useCallback((items) => {
    const result = [];
    const flatten = (arr, path = "") => {
      arr.forEach((item) => {
        if (item.request) {
          result.push({
            ...item,
            path: path ? `${path} / ${item.name}` : item.name,
          });
        } else if (item.item) {
          const newPath = path ? `${path} / ${item.name}` : item.name;
          flatten(item.item, newPath);
        }
      });
    };
    flatten(items);
    return result;
  }, []);

  const flattenedItems = useMemo(
    () => flattenItems(items),
    [items, flattenItems],
  );

  return (
    <Grid container spacing={1.5}>
      {flattenedItems.map((item, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <Box className="grid-item">
            <Box className="grid-item-header">
              <Typography
                variant="subtitle2"
                className="grid-item-title"
                title={item.name}
              >
                {item.name}
              </Typography>
              <MethodBadge method={item.request.method} />
            </Box>

            {item.path && (
              <Typography
                variant="caption"
                className="grid-item-path"
                title={item.path}
              >
                {item.path}
              </Typography>
            )}

            {item.request?.url?.raw && (
              <Typography
                variant="caption"
                className="grid-item-url"
                title={item.request.url.raw}
              >
                {item.request.url.raw}
              </Typography>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

const PostmanViewer = ({ collectionUrl, fileName }) => {
  const [collection, setCollection] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm] = useState("");
  const [selectedMethods, setSelectedMethods] = useState([
    "GET",
    "POST",
    "PUT",
    "DELETE",
    "PATCH",
  ]);
  const [viewMode, setViewMode] = useState("tree");
  const [notifications, setNotifications] = useState([]);

  const searchInputRef = useRef(null);

  const addNotification = (message, severity = "info") => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 2500);
  };

  useEffect(() => {
    if (!collectionUrl) {
      setError("URL della collezione Postman non specificato");
      return;
    }

    fetch(collectionUrl, { cache: "no-cache" })
      .then((response) => {
        if (!response.ok)
          throw new Error(
            `Impossibile caricare la collezione: ${response.status}`,
          );
        const ct = response.headers.get("content-type") || "";
        if (!ct.includes("application/json"))
          throw new Error("Formato collezione non valido");
        return response.json();
      })
      .then((data) => setCollection(data))
      .catch((err) => {
        setError(`Errore nel caricamento: ${err.message}`);
      });
  }, [collectionUrl]);

  const filterItems = useCallback(
    (items) => {
      if (!items) return [];
      if (!searchTerm && selectedMethods.length === 5) return items;

      const filterItem = (item) => {
        if (item.request) {
          const matchesSearch =
            !searchTerm ||
            item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.request.url?.raw
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item.request.description
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase());

          const matchesMethod = selectedMethods.includes(
            item.request.method?.toUpperCase(),
          );
          return matchesSearch && matchesMethod;
        }

        if (item.item) {
          const filteredSubItems = item.item
            .map((subItem) => {
              if (subItem.request) return filterItem(subItem) ? subItem : null;
              if (subItem.item) {
                const filtered = {
                  ...subItem,
                  item: subItem.item.filter(filterItem).filter(Boolean),
                };
                return filtered.item.length > 0 ? filtered : null;
              }
              return null;
            })
            .filter(Boolean);

          if (filteredSubItems.length > 0)
            return { ...item, item: filteredSubItems };
          return !searchTerm;
        }
        return false;
      };

      return items
        .map((item) =>
          filterItem(item)
            ? item.item
              ? { ...item, item: filterItems(item.item) }
              : item
            : null,
        )
        .filter(Boolean);
    },
    [searchTerm, selectedMethods],
  );

  const stats = useMemo(() => {
    if (!collection?.item) return null;
    let totalRequests = 0;
    const methodCounts = {
      GET: 0,
      POST: 0,
      PUT: 0,
      DELETE: 0,
      PATCH: 0,
      OTHER: 0,
    };

    const countRequests = (items) => {
      items.forEach((item) => {
        if (item.request) {
          totalRequests++;
          const method = item.request.method?.toUpperCase() || "OTHER";
          if (methodCounts[method] !== undefined) methodCounts[method]++;
          else methodCounts.OTHER++;
        }
        if (item.item) countRequests(item.item);
      });
    };

    countRequests(collection.item);

    return {
      totalRequests,
      methodCounts,
      totalFolders: collection.item.filter((i) => i.item).length,
    };
  }, [collection]);

  const handleExport = (format) => {
    if (!collection) return;

    let content, downloadName, mimeType;

    switch (format) {
      case "json":
        content = JSON.stringify(collection, null, 2);
        downloadName = `${collection.info?.name || "collection"}.json`;
        mimeType = "application/json";
        break;
      case "markdown":
        content = generateMarkdownDocs(collection);
        downloadName = `${collection.info?.name || "collection"}.md`;
        mimeType = "text/markdown";
        break;
      default:
        content = JSON.stringify(collection, null, 2);
        downloadName = `${collection.info?.name || "collection"}.json`;
        mimeType = "application/json";
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    addNotification(`Esportazione completata: ${downloadName}`, "success");
  };

  const generateMarkdownDocs = (collection) => {
    let md = `# ${collection.info?.name || "API Documentation"}\n\n`;
    if (collection.info?.description)
      md += `${collection.info.description}\n\n`;

    const processItems = (items, level = 2) => {
      items.forEach((item) => {
        if (item.request) {
          md += `${"#".repeat(level)} ${item.name}\n\n`;
          md += `**Method:** ${item.request.method || "UNKNOWN"}\n\n`;
          md += `**URL:** \`${item.request.url?.raw || ""}\`\n\n`;
          if (item.request.description)
            md += `**Description:** ${item.request.description}\n\n`;

          if (item.request.header?.length > 0) {
            md += `**Headers:**\n\n`;
            md += `| Key | Value | Description |\n| --- | --- | --- |\n`;
            item.request.header.forEach((h) => {
              md += `| ${h.key} | ${h.value} | ${h.description || ""} |\n`;
            });
            md += `\n`;
          }

          const queryParams = item.request.url?.query;
          if (queryParams?.length > 0) {
            md += `**Query Parameters:**\n\n`;
            md += `| Key | Value | Description |\n| --- | --- | --- |\n`;
            queryParams.forEach((p) => {
              md += `| ${p.key} | ${p.value || ""} | ${p.description || ""} |\n`;
            });
            md += `\n`;
          }

          if (item.request.body?.raw) {
            md += `**Body:**\n\n\`\`\`json\n${item.request.body.raw}\n\`\`\`\n\n`;
          }
        } else if (item.item) {
          md += `${"#".repeat(level)} ${item.name}\n\n`;
          if (item.description) md += `${item.description}\n\n`;
          processItems(item.item, level + 1);
        }
      });
    };

    processItems(collection.item);
    return md;
  };

  const filteredItems = useMemo(() => {
    if (!collection?.item) return [];
    return filterItems(collection.item);
  }, [collection, filterItems]);

  if (error) {
    return (
      <Alert
        severity="error"
        variant="outlined"
        icon={<ErrorIcon />}
        className="error-alert"
      >
        <Typography variant="subtitle2" className="error-alert-title">
          Errore di caricamento
        </Typography>
        <Typography variant="body2" className="error-alert-message">
          {error}
        </Typography>
      </Alert>
    );
  }

  return (
    <Box className="postman-container">
      {notifications.map((notif) => (
        <Snackbar
          key={notif.id}
          open
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ mt: 6 }}
        >
          <Alert
            severity={notif.severity}
            variant="filled"
          >
            {notif.message}
          </Alert>
        </Snackbar>
      ))}

      <Box className="postman-paper">
        <Box className="postman-header">
          <Box className="postman-title-row">
            <ApiIcon className="postman-icon" />
            <Typography
              variant="h5"
              component="h2"
              className="postman-title"
            >
              {collection?.info?.name || fileName || "Documentazione API"}
            </Typography>

            {collection?.info?._postman_id && (
              <Chip
                label={`ID: ${collection.info._postman_id.substring(0, 8)}...`}
                size="small"
                className="postman-id-chip"
              />
            )}
          </Box>

          {collection?.info?.description && (
            <Box sx={{ mt: 1.5 }}>
              <MarkdownPreview
                source={collection.info.description}
                style={{
                  backgroundColor: "transparent",
                  fontSize: "0.95rem",
                }}
              />
            </Box>
          )}
        </Box>

        <Box className="postman-content">
          <Grid container spacing={1.5} alignItems="start">
            <Grid item xs={12}>
              <Box className="controls-container">
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, val) => val && setViewMode(val)}
                  size="small"
                  aria-label="Modalità di visualizzazione"
                >
                  <ToggleButton
                    value="tree"
                    aria-label="Visualizzazione ad albero"
                  >
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton
                    value="grid"
                    aria-label="Visualizzazione a griglia"
                  >
                    <ViewModuleIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {["GET", "POST", "PUT", "DELETE", "PATCH"].map((method) => (
                    <Chip
                      key={method}
                      label={method}
                      size="small"
                      // color={
                      //   selectedMethods.includes(method) ? "primary" : "default"
                      // }
                      onClick={() =>
                        setSelectedMethods((prev) =>
                          prev.includes(method)
                            ? prev.filter((m) => m !== method)
                            : [...prev, method],
                        )
                      }
                      variant={
                        selectedMethods.includes(method) ? "filled" : "outlined"
                      }
                      className= {selectedMethods.includes(method) ? "method-chip" : "method-chip-selected"}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            {filteredItems.length > 0 ? (
              viewMode === "tree" ? (
                filteredItems.map((item, index) => (
                  <Box key={index}>
                    {item.request ? (
                      <ItemViewer item={item} />
                    ) : (
                      <FolderViewer folder={item} />
                    )}
                  </Box>
                ))
              ) : (
                <GridView items={filteredItems} />
              )
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Nessun endpoint trovato con i criteri di ricerca specificati
              </Alert>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PostmanViewer;